import React, { useEffect, useState } from 'react';
import {
  XMarkIcon,
  ClockIcon,
  ArrowPathIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { useCareOrderHistory, useRestoreCareOrderVersion, CareOrderHistory, useCareOrderWorkflow } from '@/services/api';
import { useAuth } from '@/state/AuthContext';
import { useToasts } from '@/state/ToastContext';

interface CareOrderHistoryModalProps {
  orderId: string;
  isOpen: boolean;
  onClose: () => void;
}

interface FieldChange {
  field: string;
  label: string;
  oldValue: string | null;
  newValue: string | null;
}

const CareOrderHistoryModal: React.FC<CareOrderHistoryModalProps> = ({
  orderId,
  isOpen,
  onClose,
}) => {
  const { user } = useAuth();
  const { pushToast } = useToasts();
  const [selectedHistoryId, setSelectedHistoryId] = useState<string | null>(null);

  const historyQuery = useCareOrderHistory(orderId);
  const history = historyQuery.data || [];
  const isLoading = historyQuery.isLoading;
  const error = historyQuery.error;
  const workflowQuery = useCareOrderWorkflow(orderId, isOpen);
  const workflow = workflowQuery.data;
  const timelineSignoffs = workflow?.signoffs ?? [];
  const timelineHandoffs = workflow?.handoffs ?? [];

  const restoreVersion = useRestoreCareOrderVersion();

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleRestore = async (version: number) => {
    if (!user?.permissions?.includes('care:update')) {
      pushToast({ message: '您沒有權限恢復版本', tone: 'error' });
      return;
    }

    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`確定要恢復到版本 ${version} 嗎？這將創建一個新版本。`)) {
      return;
    }

    try {
      await restoreVersion.mutateAsync({ orderId, version });
      pushToast({ message: '版本已恢復', tone: 'success' });
      onClose();
    } catch (error) {
      pushToast({ message: '恢復版本失敗', tone: 'error' });
    }
  };

  const getActionLabel = (action: string): string => {
    const labels: Record<string, string> = {
      created: '創建',
      updated: '更新',
      acknowledged: '確認',
      completed: '完成',
      blocked: '阻擋',
      restored: '恢復',
      deleted: '刪除',
    };
    return labels[action] || action;
  };

  const getActionColor = (action: string): string => {
    const colors: Record<string, string> = {
      created: 'text-green-600 bg-green-50',
      updated: 'text-blue-600 bg-blue-50',
      acknowledged: 'text-indigo-600 bg-indigo-50',
      completed: 'text-green-600 bg-green-50',
      blocked: 'text-red-600 bg-red-50',
      restored: 'text-purple-600 bg-purple-50',
      deleted: 'text-gray-600 bg-gray-50',
    };
    return colors[action] || 'text-gray-600 bg-gray-50';
  };

  const calculateFieldChanges = (
    currentChanges: Record<string, any>,
    previousChanges: Record<string, any> | null
  ): FieldChange[] => {
    if (!previousChanges) return [];

    const changes: FieldChange[] = [];
    const fieldLabels: Record<string, string> = {
      title: '標題',
      category: '類別',
      details: '詳細說明',
      priority: '優先級',
      status: '狀態',
      assignedRole: '指派角色',
      dueDate: '截止日期',
      blockedReason: '阻擋原因',
      acknowledgedAt: '確認時間',
      completedAt: '完成時間',
    };

    const fieldsToCheck = [
      'title',
      'category',
      'details',
      'priority',
      'status',
      'assignedRole',
      'dueDate',
      'blockedReason',
    ];

    fieldsToCheck.forEach((field) => {
      const oldVal = previousChanges[field];
      const newVal = currentChanges[field];

      if (oldVal !== newVal) {
        changes.push({
          field,
          label: fieldLabels[field] || field,
          oldValue: formatValue(oldVal),
          newValue: formatValue(newVal),
        });
      }
    });

    return changes;
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return '(空)';
    if (typeof value === 'boolean') return value ? '是' : '否';
    if (value instanceof Date) return value.toLocaleString('zh-TW');
    if (typeof value === 'string' && value.length === 0) return '(空)';
    return String(value);
  };

  const formatDateTime = (dateStr: string): string => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const getTimelineBadgeClass = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-emerald-100 text-emerald-700';
      case 'rejected':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-slate-100 text-slate-600';
    }
  };

  const formatTimelineStatus = (status: string) => {
    if (status === 'approved') return '已簽核';
    if (status === 'rejected') return '已退回';
    return '待簽核';
  };

  const getHandoffBadgeClass = (status: string) =>
    status === 'accepted' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700';

  const formatHandoffStatus = (status: string) => (status === 'accepted' ? '已接班' : '待接班');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div
        className="fixed inset-0"
        onClick={onClose}
        aria-hidden="true"
      />
      
      <div className="relative w-full max-w-4xl bg-white rounded-3xl shadow-xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-slate-200 flex-shrink-0">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold text-slate-900">
                版本歷史
              </h2>
              <p className="mt-1 text-sm text-slate-600">
                查看此指示的所有變更記錄
              </p>
            </div>
            <button
              onClick={onClose}
              className="rounded-2xl p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-700">簽核與交班</p>
                {workflowQuery.isFetching && (
                  <span className="text-[11px] text-slate-400">更新中...</span>
                )}
              </div>
              {workflowQuery.isLoading ? (
                <p className="mt-4 text-sm text-slate-500">載入簽核流程...</p>
              ) : (
                <>
                  {timelineSignoffs.length ? (
                    <ol className="mt-3 space-y-2">
                      {timelineSignoffs.map((step) => (
                        <li key={step.id} className="rounded-xl bg-white p-3 shadow-sm">
                          <div className="flex items-center justify-between text-sm font-semibold text-slate-700">
                            <span>{step.label}</span>
                            <span className={`rounded-full px-2 py-0.5 text-[11px] ${getTimelineBadgeClass(step.status ?? '')}`}>
                              {formatTimelineStatus(step.status ?? 'pending')}
                            </span>
                          </div>
                          <p className="text-[11px] text-slate-500">指派：{step.assignee || '尚未指派'}</p>
                          {step.note && <p className="text-[11px] text-slate-500">備註：{step.note}</p>}
                          {step.signedAt && (
                            <p className="text-[11px] text-slate-400">簽核於 {formatDateTime(step.signedAt)}</p>
                          )}
                          {!step.signedAt && step.slaMinutes && (
                            <p className="text-[11px] text-slate-400">
                              SLA {step.slaMinutes} 分鐘{step.slaBreached ? ' · 已逾時' : ''}
                            </p>
                          )}
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <p className="mt-4 text-sm text-slate-500">尚未建立簽核流程</p>
                  )}

                  <div className="mt-5">
                    <h4 className="text-sm font-semibold text-slate-700">交班紀錄</h4>
                    {timelineHandoffs.length ? (
                      <ul className="mt-2 space-y-2">
                        {timelineHandoffs.map((handoff) => (
                          <li key={handoff.id} className="rounded-xl bg-white p-3 text-xs shadow-sm">
                            <div className="flex items-center justify-between text-slate-700">
                              <span className="font-semibold">
                                {handoff.fromRole} → {handoff.toRole}
                              </span>
                              <span className={`rounded-full px-2 py-0.5 text-[10px] ${getHandoffBadgeClass(handoff.status)}`}>
                                {formatHandoffStatus(handoff.status)}
                              </span>
                            </div>
                            <p className="mt-1 text-[11px] text-slate-500">
                              {handoff.handoffNote || '—'}
                            </p>
                            <p className="text-[11px] text-slate-400">
                              建立：{formatDateTime(handoff.createdAt)}
                            </p>
                            {handoff.acceptedAt && (
                              <p className="text-[11px] text-slate-400">
                                接班：{formatDateTime(handoff.acceptedAt)}
                              </p>
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="mt-2 text-sm text-slate-500">尚無交班紀錄</p>
                    )}
                  </div>
                </>
              )}
            </div>
            <div>
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600"></div>
                    <p className="mt-4 text-sm text-slate-600">載入歷史記錄中...</p>
                  </div>
                </div>
              ) : error ? (
                <div className="rounded-2xl bg-red-50 p-6 text-center">
                  <p className="text-sm text-red-600">無法載入歷史記錄</p>
                </div>
              ) : !history || history.length === 0 ? (
                <div className="rounded-2xl bg-slate-50 p-6 text-center">
                  <ClockIcon className="mx-auto h-12 w-12 text-slate-400" />
                  <p className="mt-2 text-sm text-slate-600">尚無歷史記錄</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {history.map((record: CareOrderHistory, index: number) => {
                    const previousRecord = index < history.length - 1 ? history[index + 1] : null;
                    const changes = calculateFieldChanges(
                      record.changes,
                      previousRecord?.changes || null
                    );
                const isExpanded = selectedHistoryId === record.id;

                return (
                  <div
                    key={record.id}
                    className="overflow-hidden rounded-2xl border border-slate-200 bg-white"
                  >
                    {/* Timeline Header */}
                    <div
                      className="flex items-start gap-4 p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                      onClick={() =>
                        setSelectedHistoryId(isExpanded ? null : record.id)
                      }
                    >
                      {/* Timeline Dot */}
                      <div className="flex flex-col items-center flex-shrink-0">
                        <div
                          className={`flex h-10 w-10 items-center justify-center rounded-full ${getActionColor(record.action)}`}
                        >
                          {record.action === 'completed' ? (
                            <CheckCircleIcon className="h-5 w-5" />
                          ) : record.action === 'restored' ? (
                            <ArrowPathIcon className="h-5 w-5" />
                          ) : (
                            <ClockIcon className="h-5 w-5" />
                          )}
                        </div>
                        {index < history.length - 1 && (
                          <div className="mt-2 h-full w-0.5 bg-slate-200 min-h-[20px]"></div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span
                            className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${getActionColor(record.action)}`}
                          >
                            {getActionLabel(record.action)}
                          </span>
                          <span className="text-sm text-slate-600">
                            版本 {record.version}
                          </span>
                        </div>
                        <p className="mt-2 text-sm text-slate-900">
                          由 <span className="font-medium">{record.changedBy}</span>{' '}
                          於 {formatDateTime(record.changedAt)}
                        </p>
                        {changes.length > 0 && (
                          <p className="mt-1 text-xs text-slate-500">
                            {changes.length} 個欄位變更
                          </p>
                        )}
                      </div>

                      {/* Restore Button */}
                      {user?.permissions?.includes('care:update') &&
                        index > 0 && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRestore(record.version);
                            }}
                            disabled={restoreVersion.isPending}
                            className="flex-shrink-0 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-700 disabled:opacity-50"
                          >
                            <ArrowPathIcon className="inline h-4 w-4 mr-1" />
                            恢復
                          </button>
                        )}
                    </div>

                    {/* Field Changes (Expandable) */}
                    {isExpanded && changes.length > 0 && (
                      <div className="border-t border-slate-200 bg-slate-50 p-4">
                        <h4 className="mb-3 text-sm font-semibold text-slate-900">
                          變更內容
                        </h4>
                        <div className="space-y-3">
                          {changes.map((change, idx) => (
                            <div
                              key={idx}
                              className="rounded-xl bg-white border border-slate-200 p-3"
                            >
                              <p className="mb-2 text-xs font-medium text-slate-700">
                                {change.label}
                              </p>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                <div>
                                  <p className="mb-1 text-xs text-slate-500">變更前</p>
                                  <div className="rounded-lg bg-red-50 px-3 py-2 text-red-700 break-words">
                                    {change.oldValue}
                                  </div>
                                </div>
                                <div>
                                  <p className="mb-1 text-xs text-slate-500">變更後</p>
                                  <div className="rounded-lg bg-green-50 px-3 py-2 text-green-700 break-words">
                                    {change.newValue}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Full Data View (Expandable) */}
                    {isExpanded && (
                      <div className="border-t border-slate-200 bg-slate-50 p-4">
                        <h4 className="mb-3 text-sm font-semibold text-slate-900">
                          完整資料
                        </h4>
                        <div className="rounded-xl bg-slate-900 p-4 overflow-x-auto">
                          <pre className="text-xs text-slate-100">
                            {JSON.stringify(record.changes, null, 2)}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-200 flex justify-end flex-shrink-0">
          <button
            onClick={onClose}
            className="rounded-xl bg-slate-100 px-6 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-200"
          >
            關閉
          </button>
        </div>
      </div>
    </div>
  );
};

export default CareOrderHistoryModal;
